import type { JobSummary } from "~/@types/events";
import type { TDateISO } from "~/@types/TDateISO";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { START_FLOATING_TIMER_MUTATION } from "~/helpers/offline/queueMeta";
import { markQueueReplayFailed } from "~/store/offlineQueueSlice";
import tenantChanged from "~/store/tenantChanged";

export interface FloatingEntryAllocation {
	duration: number;
	taskAssignmentId: string;
	timeEntryId: string;
}

export interface ActiveTimer {
	// Kept on state so a link replayed after a reload reuses this id; wsapi dedups by timeEntryId stream id.
	autoLinkTimeEntryId: string | null;
	floatingTimeEntryId: string;
	headcount: number;
	jobId?: string | null;
	startTime: TDateISO;
	stopTime?: TDateISO;
	taskId: string | null;
	taskName: string | null;
}

export interface FloatingEntry {
	allocations: FloatingEntryAllocation[];
	duration: number;
	id: string;
	isStale: boolean;
	jobId?: string | null;
	startTime: TDateISO;
	stopTime: TDateISO;
}

export interface EventsTimerState {
	activeTimer?: ActiveTimer;
	floatingEntries: FloatingEntry[];
	scannedJob?: JobSummary;
	stopFailed: boolean;
}

const initialState: EventsTimerState = {
	floatingEntries: [],
	stopFailed: false
};

export const eventsTimerSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(markQueueReplayFailed, (state, action) => {
			const isRefusalOfActiveTimer =
				action.payload.mutationName === START_FLOATING_TIMER_MUTATION &&
				action.payload.retryable === false &&
				state.activeTimer !== undefined &&
				action.payload.reconciliationId === state.activeTimer.floatingTimeEntryId;
			if (!isRefusalOfActiveTimer) return;
			state.activeTimer = undefined;
			state.stopFailed = false;
		});
		builder.addCase(tenantChanged, (state) => {
			state.scannedJob = undefined;
		});
	},
	initialState,
	name: "eventsTimer",
	reducers: {
		addFloatingEntry: (state, action: PayloadAction<FloatingEntry>) => {
			const exists = state.floatingEntries.some((e) => e.id === action.payload.id);
			if (!exists) {
				state.floatingEntries.push({
					...action.payload,
					allocations: action.payload.allocations ?? []
				});
			}
		},
		clearActiveTimer: (state) => {
			state.activeTimer = undefined;
			state.stopFailed = false;
		},
		clearScannedJob: (state) => {
			state.scannedJob = undefined;
		},
		removeFloatingEntry: (state, action: PayloadAction<string>) => {
			state.floatingEntries = state.floatingEntries.filter((entry) => entry.id !== action.payload);
		},
		setActiveTimer: (state, action: PayloadAction<ActiveTimer | null>) => {
			state.activeTimer = action.payload === null ? undefined : { ...action.payload };
			state.stopFailed = false;
		},
		setFloatingEntryAllocations: (
			state,
			action: PayloadAction<{ allocations: FloatingEntryAllocation[]; entryId: string }>
		) => {
			const entry = state.floatingEntries.find((e) => e.id === action.payload.entryId);
			if (entry) {
				entry.allocations = action.payload.allocations;
			}
		},
		setScannedJob: (state, action: PayloadAction<JobSummary | null>) => {
			state.scannedJob = action.payload === null ? undefined : { ...action.payload };
		},
		setStopFailed: (state, action: PayloadAction<boolean>) => {
			state.stopFailed = action.payload;
		},
		updateActiveTimer: (state, action: PayloadAction<Partial<ActiveTimer>>) => {
			if (!action.payload) {
				state.activeTimer = undefined;
			} else {
				if (!state.activeTimer) return;
				state.activeTimer = { ...state.activeTimer, ...action.payload };
			}
		},
		updateScannedJob: (state, action: PayloadAction<Partial<JobSummary>>) => {
			if (!action.payload) {
				state.scannedJob = undefined;
			} else {
				if (!state.scannedJob) return;
				state.scannedJob = { ...state.scannedJob, ...action.payload };
			}
		}
	}
});

export const {
	addFloatingEntry,
	clearActiveTimer,
	clearScannedJob,
	removeFloatingEntry,
	setActiveTimer,
	setFloatingEntryAllocations,
	setScannedJob,
	setStopFailed,
	updateActiveTimer,
	updateScannedJob
} = eventsTimerSlice.actions;

export default eventsTimerSlice.reducer;
